import React, { useEffect, useState, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogDetail, getBlogList } from '../services/Api';
import { User } from 'lucide-react';
import Footer from '../components/Footer';
import { GlobalContext } from '../GlobalContext';
import { Helmet } from 'react-helmet';


export default function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [latestPosts, setLatestPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { globalVar } = useContext(GlobalContext);
  const stripHtml = (html) => html?.replace(/<[^>]+>/g, '') || '';


  const getImageUrl = (path) => `${globalVar}/${path}`;

function fixInternalLinks(content) {
  // Fix internal links in content
  return content.replace(/<a href="([^"]+)">([^<]+)<\/a>/g, (match, url, text) => {
    // Check if the URL is missing or broken and fix it
    if (!url || !text) return match;

    // If the URL is broken or malformed, fix it
    const fixedUrl = url.startsWith("http") ? url : `https://justoffplan.ai/${url}`;

    // Return the corrected anchor tag
    return `<a href="${fixedUrl}" target="_blank" rel="noopener noreferrer">${text}</a>`;
  });
}

function addIdsToHeadings(html) {
  let index = 0;
  let fixedHtml = html.replace(/<h2[^>]*>(.*?)<\/h2>/g, (match, content) => {
    const id = `heading-${index++}-${content.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9\-]/g, '')}`;
    return `<h2 id="${id}" class="scroll-mt-24">${content}</h2>`;
  });
  return fixInternalLinks(fixedHtml);
}



  useEffect(() => {
    const fetchPostDetail = async () => {
      setIsLoading(true);
      const res = await getBlogDetail(slug);
      if (res.success) {
        setPost(res.data);
      }
      setIsLoading(false);
    };

    const fetchLatestPosts = async () => {
      const res = await getBlogList(1, 3);
      if (res.success) {
        setLatestPosts(res.data.results);
      }
    };

    if (slug) {
      fetchPostDetail();
      fetchLatestPosts();
    }
  }, [slug]);

  const handleShare = () => {
    if (navigator.share) {
      navigator
        .share({
          title: post?.title || 'Blog Post',
          url: window.location.href,
        })
        .catch((err) => console.log('Error sharing:', err));
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (isLoading || !post) return <div className="text-center py-20">Loading...</div>;


  

  return (

    
    <div className="min-h-screen bg-white">
{!isLoading && post && (
  <Helmet>
    <title>{post.meta_title || post.title}</title>
    <meta name="description" content={post.meta_description || stripHtml(post.description).substring(0, 150)} />
    <meta property="og:description" content={post.meta_description || stripHtml(post.description).substring(0, 150)} />
    <meta property="og:title" content={post.meta_title || post.title} />
    <meta property="og:image" content={post.image ? getImageUrl(post.image) : ''} />
    <meta property="og:url" content={window.location.href} />
    <link rel="canonical" href={window.location.href} />

  </Helmet>
)}


      <div className="max-w-screen-xl mx-auto px-4 md:px-6 lg:px-8 py-12 lg:py-20">
        <article className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            <header className="space-y-6">
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 leading-tight mt-14 sm:mb-6">{post.title}</h1>

              <div className="flex items-center gap-4">
                {post.author_image ? (
                  <img
                    src={post.author_image}
                    alt={post.author_name}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                    <User className="h-5 w-5 text-blue-600" />
                  </div>
                )}
                <div>
                  <p className="text-sm font-medium text-slate-700">{post.author_name}</p>
                  <time className="text-xs text-slate-500">
                    {new Date(post.created_at).toLocaleDateString()}
                  </time>
                </div>
              </div>
            </header>

            {post.image && (
              <img
                src={getImageUrl(post.image.replace(/_[^/]+(?=\.\w+$)/, ''))}
                alt="off-plan properties Dubai"
                className="w-full rounded-xl shadow-md"
              />
            )}

            {/* Description without card-style */}
<section
  className="prose prose-lg max-w-none text-slate-800 
    [&_h1]:text-4xl [&_h1]:font-bold [&_h1]:text-slate-900
    [&_h2]:text-3xl [&_h2]:font-semibold [&_h2]:text-slate-800
    [&_h3]:text-2xl [&_h3]:font-semibold [&_h3]:text-slate-700
    [&_p]:text-base [&_p]:leading-relaxed [&_p]:my-4
    [&_ul]:list-disc [&_ul]:pl-6 [&_li]:mb-1
    [&_a]:text-teal-500 [&_a:hover]:underline
    [&_blockquote]:border-l-4 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-slate-600
    [&_img]:rounded-lg [&_img]:shadow-sm"
>
   <div dangerouslySetInnerHTML={{ __html: addIdsToHeadings(post.description) }} />

</section>



            {/* Full Content */}


            {/* Footer Actions */}
            <div className="pt-8 border-t border-slate-200 flex justify-between">
              <Link to="/blog" className="text-blue-600 hover:underline">
                ← Back to all stories
              </Link>
              <button onClick={handleShare} className="text-blue-600 hover:underline">
                Share
              </button>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-12 sticky top-24 self-start">
            {/* Table of Contents */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Table of Contents</h3>
<ul className="text-sm text-slate-600 space-y-2 list-disc list-inside">
  {(post.description?.match(/<h2[^>]*>(.*?)<\/h2>/g) || []).map((heading, index) => {
    const plainText = heading.replace(/<[^>]+>/g, '');
    const id = `heading-${index}-${plainText.replace(/\s+/g, '-').toLowerCase().replace(/[^a-z0-9\-]/g, '')}`;
    return (
<li key={index}>
  <a
    href={`#${id}`}
    className="hover:underline text-blue-600"
    onClick={(e) => {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        // Optional: update hash without default jump
        window.history.pushState(null, '', `#${id}`);
      }
    }}
  >
    {plainText}
  </a>
</li>

    );
  })}
</ul>





            </section>

            {/* Latest Articles */}
            <section>
              <h3 className="text-lg font-semibold text-slate-800 mb-3">Latest Articles</h3>
              <ul className="space-y-3">
                {latestPosts.map((item) => (
  <li key={item.id} className="flex items-start gap-4">
    {item.image && (
      <img
        src={getImageUrl(item.image)}
        alt={item.title}
        className="w-16 h-16 object-cover rounded"
      />
    )}
    <div>
      <Link
        to={`/blog/${item.slug}`}
        className="text-sm font-medium text-blue-700 hover:underline"
      >
        {item.title}
      </Link>
      <p className="text-xs text-slate-500">
        {new Date(item.created_at).toLocaleDateString()}
      </p>
    </div>
  </li>
))}

              </ul>
            </section>
          </aside>
          
        </article>
      </div>
     <Footer
  ctaData={
    post?.cta_heading
      ? {
          heading: post.cta_heading,
          subheading: post.cta_subheading,
          buttonText: post.cta_button_text,
          buttonLink: post.cta_button_link
        }
      : undefined
  }
/>

    </div>
  );
}
